<template>
  <div class="modern-container">
    <div class="page-header">
      <div class="header-content">
        <h2 class="page-title">
          Ejecuciones en Tiendas
        </h2>
        <p class="page-subtitle">
          Gestiona los focos de ejecución para el equipo de campo
        </p>
      </div>
      <div class="header-actions">
        <b-button
          variant="outline-secondary"
          class="action-btn"
          @click="goBack"
        >
          <i class="fa fa-arrow-left mr-2" />
          Volver
        </b-button>
        <b-button
          variant="primary"
          class="action-btn"
          @click="goToCreate"
        >
          <i class="fa fa-plus mr-2" />
          Nueva Ejecución
        </b-button>
      </div>
    </div>

    <!-- Filtros -->
    <div class="filters-bar">
      <div class="filter-group">
        <label class="filter-label">Estado</label>
        <div class="btn-group">
          <button
            class="filter-btn"
            :class="{ active: filters.active === null }"
            @click="setActiveFilter(null)"
          >
            Todos
          </button>
          <button
            class="filter-btn"
            :class="{ active: filters.active === true }"
            @click="setActiveFilter(true)"
          >
            Activos
          </button>
          <button
            class="filter-btn"
            :class="{ active: filters.active === false }"
            @click="setActiveFilter(false)"
          >
            Inactivos
          </button>
        </div>
      </div>

      <div class="filter-group">
        <label class="filter-label">Desde</label>
        <input
          v-model="filters.start_date"
          type="date"
          class="filter-date"
          @change="load"
        >
      </div>

      <div class="filter-group">
        <label class="filter-label">Hasta</label>
        <input
          v-model="filters.end_date"
          type="date"
          class="filter-date"
          @change="load"
        >
      </div>

      <button
        v-if="hasActiveFilters"
        class="clear-filters-btn"
        @click="clearFilters"
      >
        <i class="fa fa-times mr-1" />
        Limpiar filtros
      </button>
    </div>

    <!-- Skeleton -->
    <div
      v-if="loading"
      class="modern-card"
    >
      <div class="skeleton-rows">
        <div
          v-for="n in 5"
          :key="n"
          class="skeleton-row"
        >
          <div class="skeleton-text long shimmer" />
          <div class="skeleton-text medium shimmer" />
          <div class="skeleton-text short shimmer" />
          <div class="skeleton-text short shimmer" />
          <div class="skeleton-badge shimmer" />
        </div>
      </div>
    </div>

    <!-- Tabla -->
    <div
      v-else
      class="modern-card"
    >
      <b-table
        :items="items"
        :fields="fields"
        class="modern-table"
        thead-class="modern-thead"
        tbody-class="modern-tbody"
        responsive
        show-empty
        empty-text="No hay ejecuciones que coincidan con los filtros."
      >
        <template #cell(active)="{ item }">
          <span
            class="status-badge"
            :class="item.active ? 'badge-active' : 'badge-inactive'"
          >
            {{ item.active ? 'Activa' : 'Inactiva' }}
          </span>
        </template>

        <template #cell(start_date)="{ item }">
          {{ formatDate(item.start_date) }}
        </template>

        <template #cell(end_date)="{ item }">
          {{ item.end_date ? formatDate(item.end_date) : '—' }}
        </template>

        <template #cell(actions)="{ item }">
          <div class="table-actions">
            <b-button
              v-b-tooltip.hover
              title="Ver resultados"
              size="sm"
              variant="outline-secondary"
              class="table-action-btn"
              @click="goToResults(item.id)"
            >
              <i class="fa fa-bar-chart" />
            </b-button>
            <b-button
              v-b-tooltip.hover
              title="Eliminar definitivamente"
              size="sm"
              variant="outline-danger"
              class="table-action-btn"
              :disabled="actionBusy"
              @click="openDeleteModal(item)"
            >
              <i class="fa fa-trash" />
            </b-button>
          </div>
        </template>
      </b-table>
    </div>

    <!-- Error -->
    <div
      v-if="error"
      class="error-alert"
    >
      <i class="fa fa-exclamation-circle mr-2" />
      {{ error }}
      <button
        class="retry-btn"
        @click="load"
      >
        Reintentar
      </button>
    </div>

    <BaseModal
      :show="showDeactivateModal"
      title="Desactivar ejecución"
      confirm-text="Desactivar"
      confirm-variant="warning"
      :processing="deactivating"
      processing-text="Desactivando..."
      @confirm="handleDeactivate"
      @update:show="showDeactivateModal = $event"
      @hidden="focusToDeactivate = null"
    >
      <p class="mb-0">
        ¿Deseas desactivar
        <strong v-if="focusToDeactivate">
          {{ focusToDeactivate.name }}
        </strong>
        <template v-else>
          esta ejecución
        </template>
        ?
        Quedará como inactiva y podrás verla con el filtro «Inactivos».
      </p>
    </BaseModal>

    <BaseModal
      :show="showDeleteModal"
      title="Eliminar ejecución"
      confirm-text="Eliminar"
      confirm-variant="danger"
      :processing="deleting"
      processing-text="Eliminando..."
      @confirm="handleDelete"
      @update:show="showDeleteModal = $event"
      @hidden="focusToDelete = null"
    >
      <p class="mb-0">
        ¿Eliminar de forma permanente
        <strong v-if="focusToDelete">
          {{ focusToDelete.name }}
        </strong>
        <template v-else>
          esta ejecución
        </template>
        ?
        Esta acción no se puede deshacer.
      </p>
    </BaseModal>
  </div>
</template>

<script>
import { ref, computed, onMounted } from '@vue/composition-api'
import BaseModal from '@/app/shared/components/BaseModal.vue'
import { getExecutionFocusList, deactivateExecutionFocus, deleteExecutionFocus } from '../Services'

export default {
  name: 'ExecutionFocusList',
  components: {
    BaseModal
  },
  setup (_, context) {
    const loading = ref(false)
    const error = ref(null)
    const items = ref([])
    const deactivating = ref(false)
    const deleting = ref(false)
    const showDeactivateModal = ref(false)
    const showDeleteModal = ref(false)
    const focusToDeactivate = ref(null)
    const focusToDelete = ref(null)

    const actionBusy = computed(() => deactivating.value || deleting.value)

    const filters = ref({
      active: null,
      start_date: '',
      end_date: ''
    })

    const fields = [
      { key: 'name', label: 'Nombre', sortable: true },
      { key: 'description', label: 'Descripción', sortable: false },
      { key: 'start_date', label: 'Inicio', sortable: true },
      { key: 'end_date', label: 'Fin', sortable: true },
      { key: 'active', label: 'Estado', sortable: true },
      { key: 'actions', label: '', thStyle: { width: '150px' } }
    ]

    const hasActiveFilters = computed(() =>
      filters.value.active !== null ||
      !!filters.value.start_date ||
      !!filters.value.end_date
    )

    const load = async () => {
      loading.value = true
      error.value = null
      try {
        const f = filters.value
        const params = {}
        if (f.active !== null) params.active = f.active
        if (f.start_date) params.start_date = f.start_date
        if (f.end_date) params.end_date = f.end_date
        items.value = await getExecutionFocusList(params)
      } catch (e) {
        error.value = e.message || 'Error al cargar las ejecuciones'
      } finally {
        loading.value = false
      }
    }

    const setActiveFilter = (value) => {
      filters.value.active = value
      load()
    }

    const clearFilters = () => {
      filters.value = { active: null, start_date: '', end_date: '' }
      load()
    }

    const formatDate = (dateStr) => {
      if (!dateStr) return '—'
      const [y, m, d] = dateStr.split('-')
      return `${d}/${m}/${y}`
    }

    const goBack = () => context.root.$router.go(-1)
    const goToCreate = () => context.root.$router.push({ name: 'CreateExecutionFocus' })
    const goToEdit = (id) => context.root.$router.push({ name: 'CreateExecutionFocus', params: { id } })
    const goToResults = (id) => context.root.$router.push({ name: 'ExecutionFocusDashboard', params: { id } })

    const makeToast = (body, title, variant = 'danger') => {
      context.root.$bvToast.toast(body, { title, autoHideDelay: 5000, appendToast: false, solid: true, variant })
    }

    const openDeactivateModal = (item) => {
      focusToDeactivate.value = item
      showDeactivateModal.value = true
    }

    const handleDeactivate = async () => {
      if (!focusToDeactivate.value || deactivating.value) return
      deactivating.value = true
      try {
        await deactivateExecutionFocus(focusToDeactivate.value.id)
        showDeactivateModal.value = false
        focusToDeactivate.value = null
        makeToast('La ejecución se desactivó correctamente.', 'Éxito', 'success')
        await load()
      } catch (err) {
        const text = (err && err.message) || (err && err.data && err.data.message) || 'No se pudo desactivar la ejecución.'
        makeToast(text, 'Error')
      } finally {
        deactivating.value = false
      }
    }

    const openDeleteModal = (item) => {
      focusToDelete.value = item
      showDeleteModal.value = true
    }

    const handleDelete = async () => {
      if (!focusToDelete.value || deleting.value) return
      deleting.value = true
      try {
        await deleteExecutionFocus(focusToDelete.value.id)
        showDeleteModal.value = false
        focusToDelete.value = null
        makeToast('La ejecución se eliminó correctamente.', 'Éxito', 'success')
        await load()
      } catch (err) {
        const text = (err && err.message) || (err && err.data && err.data.message) || 'No se pudo eliminar la ejecución.'
        makeToast(text, 'Error')
      } finally {
        deleting.value = false
      }
    }

    onMounted(load)

    return {
      loading,
      error,
      items,
      deactivating,
      deleting,
      actionBusy,
      showDeactivateModal,
      showDeleteModal,
      focusToDeactivate,
      focusToDelete,
      filters,
      fields,
      hasActiveFilters,
      load,
      setActiveFilter,
      clearFilters,
      formatDate,
      goBack,
      goToCreate,
      goToEdit,
      goToResults,
      openDeactivateModal,
      handleDeactivate,
      openDeleteModal,
      handleDelete
    }
  }
}
</script>

<style scoped>
.modern-container {
  padding: 2rem 1rem;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
}

/* Page header */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  background: white;
  padding: 1.5rem 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07);
  gap: 1.5rem;
  flex-wrap: wrap;
}

.page-title {
  font-size: 1.875rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
}

.page-subtitle {
  font-size: 1rem;
  color: #718096;
  margin: 0.25rem 0 0 0;
}

.header-actions {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

.action-btn {
  padding: 0.75rem 1.5rem;
  font-weight: 500;
  border-radius: 8px;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.action-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

/* Filters */
.filters-bar {
  display: flex;
  align-items: flex-end;
  gap: 1.25rem;
  flex-wrap: wrap;
  background: white;
  padding: 1rem 1.5rem;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07);
  margin-bottom: 1.5rem;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.filter-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #4a5568;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin: 0;
}

.btn-group {
  display: flex;
}

.filter-btn {
  padding: 0.375rem 0.875rem;
  font-size: 0.8rem;
  font-weight: 500;
  border: 1px solid #e2e8f0;
  background: white;
  color: #4a5568;
  cursor: pointer;
  transition: all 0.15s;
}

.filter-btn:first-child { border-radius: 6px 0 0 6px; }
.filter-btn:last-child  { border-radius: 0 6px 6px 0; border-left: none; }
.filter-btn:not(:first-child):not(:last-child) { border-left: none; }

.filter-btn.active {
  background: #4299e1;
  border-color: #4299e1;
  color: white;
}

.filter-date {
  padding: 0.375rem 0.625rem;
  font-size: 0.8rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  color: #2d3748;
  outline: none;
  transition: border-color 0.15s;
}

.filter-date:focus {
  border-color: #4299e1;
  box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.15);
}

.clear-filters-btn {
  padding: 0.375rem 0.875rem;
  font-size: 0.8rem;
  font-weight: 500;
  border: 1px solid #e2e8f0;
  background: none;
  color: #718096;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s;
  align-self: flex-end;
}

.clear-filters-btn:hover {
  border-color: #a0aec0;
  color: #2d3748;
}

/* Card */
.modern-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07);
  overflow: hidden;
}

/* Skeleton */
.skeleton-rows {
  padding: 0;
}

.skeleton-row {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #f7fafc;
}

@keyframes shimmer {
  0%   { background-position: -200px 0; }
  100% { background-position: calc(200px + 100%) 0; }
}

.shimmer {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
  flex-shrink: 0;
}

.skeleton-text { height: 0.85rem; }
.skeleton-text.short  { width: 80px; }
.skeleton-text.medium { width: 140px; }
.skeleton-text.long   { width: 200px; }
.skeleton-badge { width: 60px; height: 1.4rem; border-radius: 20px; }

/* Table */
.modern-table {
  background: white;
  border: none;
  border-radius: 0;
  overflow: hidden;
  box-shadow: none;
  margin: 0;
}

.modern-table >>> .modern-thead th {
  background: #f7fafc;
  border: none;
  color: #4a5568;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.05em;
  padding: 1rem 1.25rem;
}

.modern-table >>> .modern-tbody td {
  border: none;
  padding: 1rem 1.25rem;
  vertical-align: middle;
  border-bottom: 1px solid #e2e8f0;
  color: #2d3748;
  font-size: 0.875rem;
}

.modern-table >>> .modern-tbody tr:last-child td {
  border-bottom: none;
}

.modern-table >>> .modern-tbody tr:hover {
  background: #f8fafc;
}

/* Status badge */
.status-badge {
  display: inline-block;
  padding: 0.2rem 0.65rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
}

.badge-active {
  background: rgba(72, 187, 120, 0.12);
  color: #276749;
}

.badge-inactive {
  background: rgba(160, 174, 192, 0.15);
  color: #4a5568;
}

/* Table actions */
.table-actions {
  display: flex;
  gap: 0.4rem;
  justify-content: center;
}

.table-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border-radius: 8px;
  transition: all 0.2s ease;
}

/* Error */
.error-alert {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
  padding: 0.875rem 1.25rem;
  background: rgba(229, 62, 62, 0.08);
  border: 1px solid rgba(229, 62, 62, 0.2);
  border-radius: 8px;
  color: #c53030;
  font-size: 0.875rem;
}

.retry-btn {
  margin-left: auto;
  background: none;
  border: 1px solid #c53030;
  color: #c53030;
  border-radius: 6px;
  padding: 0.25rem 0.75rem;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.15s;
}

.retry-btn:hover {
  background: #c53030;
  color: white;
}

@media (max-width: 768px) {
  .modern-container { padding: 1rem 0.5rem; }
  .page-header { flex-direction: column; align-items: flex-start; }
  .filters-bar { gap: 0.75rem; }
}
</style>



// WEBPACK FOOTER //
// src/app/news_feed/views/ExecutionFocusList.vue